import { sortDeliveryPointByAddressComparator } from '../businessRelations/helpers';
import useDeliveryPoints from '../businessRelations/useDeliveryPoints';
import useCart from './useCart';
import { useEnrichedCartItems } from './useEnrichCartItems';

const useItemsGroupsByDeliveryPoint = () => {
  const { cart } = useCart();
  const { deliveryPoints } = useDeliveryPoints();
  const items = useEnrichedCartItems(cart?.items);

  // Group items
  const groups = groupItemsByDeliveryPoint(items, deliveryPoints);

  // Sort by delivery point
  const deliveryPointGroupsSorted = sort(groups.deliveryPoints);

  return {
    groups: {
      none: groups.none,
      deliveryPoints: deliveryPointGroupsSorted,
    },
  };
};

export default useItemsGroupsByDeliveryPoint;

type GroupedItems = {
  none: CartItemDto[];
  deliveryPoints: Map<DeliveryPointDto, CartItemDto[]>;
};

function groupItemsByDeliveryPoint(
  items: CartItemDto[] = [],
  deliveryPoints: DeliveryPointDto[] = []
): GroupedItems {
  const emptyGroups = {
    none: [],
    deliveryPoints: new Map(),
  };
  const findDeliveryPointById = findDeliveryPoint(deliveryPoints);

  return (
    items.reduce<GroupedItems>((groups, item) => {
      const deliveryPoint = findDeliveryPointById(item.deliveryPointEntityId);

      if (deliveryPoint) {
        // Delivery point set on item
        if (groups.deliveryPoints.has(deliveryPoint)) {
          groups.deliveryPoints.get(deliveryPoint)?.push(item);
        } else {
          groups.deliveryPoints.set(deliveryPoint, [item]);
        }
      } else {
        // No delivery point set on item
        groups.none.push(item);
      }
      return groups;
    }, emptyGroups) || emptyGroups
  );
}

function sort(groups: Map<DeliveryPointDto, CartItemDto[]>) {
  return new Map(
    Array.from(groups.entries()).sort(([deliveryPointA], [deliveryPointB]) =>
      sortDeliveryPointByAddressComparator(deliveryPointA, deliveryPointB)
    )
  );
}

const findDeliveryPoint =
  (deliveryPoints: DeliveryPointDto[]) => (deliveryPointId?: string) =>
    deliveryPoints.find(
      (deliveryPoint) => deliveryPoint.id === deliveryPointId
    );
